import type { User } from "../api/users";
import { hasSetupSearchCode, type UserSetupSearch } from "./user-setup-search";
import { hasPendingOrgJoin } from "./organization-setup-stub";

export type SetupTask = "email" | "phone" | "organization";

export const SETUP_TASK_ORDER: SetupTask[] = ["email", "phone", "organization"];

export function isEmailSetupComplete(
  user: User | null | undefined,
  _search?: UserSetupSearch,
): boolean {
  return Boolean(user?.emailVerified);
}

export function isPhoneSetupComplete(user: User | null | undefined): boolean {
  return Boolean(user?.phoneVerified);
}

export function isCompanySetupComplete(user: User | null | undefined): boolean {
  if (Boolean(user?.hasOrganizationMembership)) return true;
  if (Boolean(user?.hasPendingOrganizationMembership)) return true;
  // Product-only invitees can finish setup without joining an organization.
  if (Boolean(user?.hasProductAccess)) return true;
  return hasPendingOrgJoin();
}

export function isTaskComplete(
  task: SetupTask,
  user: User | null | undefined,
  search: UserSetupSearch,
): boolean {
  switch (task) {
    case "email":
      return isEmailSetupComplete(user, search);
    case "phone":
      return isPhoneSetupComplete(user);
    case "organization":
      return isCompanySetupComplete(user);
    default:
      return false;
  }
}

export function getSetupProgress(
  user: User | null | undefined,
  search: UserSetupSearch,
): { completed: number; total: number; percent: number } {
  const total = SETUP_TASK_ORDER.length;
  const completed = SETUP_TASK_ORDER.filter((t) => isTaskComplete(t, user, search)).length;
  // Baseline 25% so empty state still reads as a progress bar; +25% per completed task.
  const percent = Math.min(100, (completed + 1) * 25);
  return { completed, total, percent };
}

export function getFirstIncompleteTask(
  user: User | null | undefined,
  search: UserSetupSearch,
): SetupTask {
  if (hasSetupSearchCode(search)) return "email";
  for (const task of SETUP_TASK_ORDER) {
    if (!isTaskComplete(task, user, search)) return task;
  }
  return "organization";
}

export function getNextIncompleteTask(
  after: SetupTask,
  user: User | null | undefined,
  search: UserSetupSearch,
): SetupTask | null {
  const startIdx = SETUP_TASK_ORDER.indexOf(after);
  for (let i = startIdx + 1; i < SETUP_TASK_ORDER.length; i++) {
    const task = SETUP_TASK_ORDER[i];
    if (!isTaskComplete(task, user, search)) return task;
  }
  for (const task of SETUP_TASK_ORDER) {
    if (!isTaskComplete(task, user, search)) return task;
  }
  return null;
}

/** Next incomplete task after `current` in setup order (for skip); null if none remain. */
export function getSkipTargetTask(
  current: SetupTask,
  user: User | null | undefined,
  search: UserSetupSearch,
): SetupTask | null {
  const startIdx = SETUP_TASK_ORDER.indexOf(current);
  for (let i = startIdx + 1; i < SETUP_TASK_ORDER.length; i++) {
    const task = SETUP_TASK_ORDER[i];
    if (!isTaskComplete(task, user, search)) return task;
  }
  return null;
}

export function getNextIncompleteTaskLabel(task: SetupTask): string {
  switch (task) {
    case "phone":
      return "Verify Phone";
    case "organization":
      return "Join Organization";
    default:
      return "Continue";
  }
}

export function isEmailCodeExpired(user: User | null | undefined): boolean {
  if (!user?.emailVerificationExpiresAt || user.emailVerified) return false;
  const expires = new Date(user.emailVerificationExpiresAt);
  return !Number.isNaN(expires.getTime()) && expires <= new Date();
}

export function formatVerificationExpiry(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function isPhoneCodeExpired(user: User | null | undefined): boolean {
  if (!user?.phoneVerificationExpiresAt || user.phoneVerified) return false;
  const expires = new Date(user.phoneVerificationExpiresAt);
  return !Number.isNaN(expires.getTime()) && expires <= new Date();
}

/** @deprecated Use getFirstIncompleteTask */
export function getFirstIncompleteSetupStep(
  user: User | null | undefined,
  search: UserSetupSearch,
): number {
  const task = getFirstIncompleteTask(user, search);
  const map: Record<SetupTask, number> = { email: 1, phone: 2, organization: 3 };
  if (
    isTaskComplete("email", user, search) &&
    isTaskComplete("phone", user, search) &&
    isTaskComplete("organization", user, search)
  ) {
    return 4;
  }
  return map[task];
}

export function setupTaskFromLegacyStep(step: number): SetupTask {
  if (step <= 1) return "email";
  if (step === 2) return "phone";
  return "organization";
}

export function getPrimaryOrganizationName(user: User | null | undefined): string | null {
  if (!user?.organizations?.length) return null;
  const primary = user.organizations.find((o) => o.isPrimary);
  return (primary ?? user.organizations[0])?.name ?? null;
}
